import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { Search, Keyboard, User, Phone, Hash, ArrowRight } from "lucide-react";

/* ================================================================
 * Global keyboard-shortcut system + Quick-Search + Help modal.
 * Registered once at the app root inside AuthProvider so that any
 * authenticated page benefits automatically.
 * ================================================================ */

// destination — where a selected patient should be routed on Enter
const QUICK_MODES = {
  search: { title: "Quick Patient Search", action: "profile", hint: "Ctrl+Q" },
  opd:    { title: "New OPD — Find or Register Patient", action: "opd", hint: "Ctrl+N" },
  ipd:    { title: "New IPD — Find or Register Patient", action: "ipd", hint: "Ctrl+I" },
  bill:   { title: "Billing — Find Patient", action: "billing", hint: "Ctrl+B" },
  find:   { title: "Find Existing Patient", action: "profile", hint: "Ctrl+F" },
  global: { title: "Global Search — patients, OPD numbers, UHIDs", action: "profile", hint: "Ctrl+K" },
};

const SHORTCUTS = [
  { keys: "Ctrl + N", label: "New OPD" },
  { keys: "Ctrl + I", label: "New IPD / Admission" },
  { keys: "Ctrl + B", label: "Billing / Payment" },
  { keys: "Ctrl + P", label: "Print Center" },
  { keys: "Ctrl + F", label: "Find Existing Patient" },
  { keys: "Ctrl + R", label: "Register New Patient" },
  { keys: "Ctrl + L", label: "Lab / Investigation" },
  { keys: "Ctrl + M", label: "Medicines / Prescription" },
  { keys: "Ctrl + D", label: "Patient Dashboard" },
  { keys: "Ctrl + Q", label: "Quick Patient Search" },
  { keys: "Ctrl + K", label: "Global Search" },
  { keys: "Ctrl + /", label: "Show Keyboard Shortcuts" },
  { keys: "Esc",     label: "Close any modal / popup" },
];

const ShortcutsContext = createContext(null);
export const useShortcuts = () => useContext(ShortcutsContext);

export default function ShortcutsProvider({ children }) {
  const nav = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [quickMode, setQuickMode] = useState(null); // one of QUICK_MODES keys or null
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [hi, setHi] = useState(0); // highlighted index
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus helper — auto-focus a data-shortcut-focus element after route change.
  const focusFirstField = useCallback((selectorHint) => {
    setTimeout(() => {
      const el = document.querySelector(selectorHint || "[data-shortcut-focus='true']");
      if (el && typeof el.focus === "function") el.focus();
    }, 260);
  }, []);

  const openQuick = useCallback((mode) => {
    setQuickMode(mode);
    setQ("");
    setResults([]);
    setHi(0);
    setTimeout(() => searchRef.current?.focus(), 60);
  }, []);

  const closeAllModals = useCallback(() => {
    // Close our modals
    setQuickMode(null);
    setHelpOpen(false);
    // Close any Radix / shadcn Dialog by simulating an Escape event on document
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
  }, []);

  const goTo = useCallback((path, focusSel) => {
    nav(path);
    focusFirstField(focusSel);
  }, [nav, focusFirstField]);

  // -------- Search fetch (debounced) --------
  useEffect(() => {
    if (!quickMode) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients", { params: { q: q.trim() } });
        // Client-side filter fallback in case backend ignores q
        const needle = q.trim().toLowerCase();
        const filtered = (Array.isArray(data) ? data : []).filter((p) =>
          (p.name || "").toLowerCase().includes(needle) ||
          (p.mobile || "").includes(needle) ||
          (p.uhid || "").toLowerCase().includes(needle) ||
          (p.abha_number || "").includes(needle)
        );
        setResults(filtered.slice(0, 10));
        setHi(0);
      } catch { setResults([]); }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, quickMode]);

  // -------- Select a patient (from quick search) --------
  const pick = useCallback(async (patient) => {
    if (!patient) return;
    const mode = QUICK_MODES[quickMode];
    setQuickMode(null);
    switch (mode?.action) {
      case "opd":
        // `_t` nonce guarantees search params change even when same patient picked twice.
        nav(`/opd?new=1&patient=${patient.id}&_t=${Date.now()}`);
        break;
      case "ipd":
        nav(`/ipd?new=1&patient=${patient.id}&_t=${Date.now()}`);
        break;
      case "billing":
        nav(`/billing?patient=${patient.id}&_t=${Date.now()}`);
        break;
      case "profile":
      default:
        nav(`/patients/${patient.id}`);
    }
  }, [quickMode, nav]);

  // -------- Global key handler --------
  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
             el.isContentEditable;
    };

    const onKey = (e) => {
      // Esc — always allowed, closes any modal
      if (e.key === "Escape") {
        if (quickMode || helpOpen) { e.preventDefault(); closeAllModals(); }
        return;
      }

      // Ctrl-shortcuts fire even inside inputs (intentional per spec)
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) {
        // Non-ctrl keys are ignored inside inputs
        if (isEditable(document.activeElement)) return;
        return;
      }

      const key = e.key.toLowerCase();
      const map = {
        n: () => openQuick("opd"),
        i: () => openQuick("ipd"),
        b: () => openQuick("bill"),
        p: () => { e.preventDefault(); goTo(`/print-center?_t=${Date.now()}`); },
        f: () => openQuick("find"),
        r: () => { e.preventDefault(); goTo(`/patients?new=1&_t=${Date.now()}`, "[data-shortcut-focus='patient-name'], [data-shortcut-focus='true']"); },
        l: () => { e.preventDefault(); goTo(`/investigations?_t=${Date.now()}`); },
        m: () => { e.preventDefault(); goTo(`/pharmacy?_t=${Date.now()}`); },
        d: () => openQuick("search"),
        q: () => openQuick("search"),
        k: () => openQuick("global"),
        "/": () => { e.preventDefault(); setHelpOpen(true); },
      };

      const fn = map[key];
      if (fn) {
        e.preventDefault();
        e.stopPropagation();
        fn();
      }
    };

    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [quickMode, helpOpen, closeAllModals, openQuick, goTo]);

  // -------- Result-list keyboard nav --------
  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (results[hi]) pick(results[hi]);
      else if (["opd", "ipd", "bill"].includes(quickMode) && q.trim().length >= 2) {
        // No match — offer register flow with prefilled query
        setQuickMode(null);
        nav(`/patients?new=1&prefill=${encodeURIComponent(q.trim())}&_t=${Date.now()}`);
      }
    }
  };

  const ctx = useMemo(() => ({ openQuick, closeAllModals, showHelp: () => setHelpOpen(true) }), [openQuick, closeAllModals]);

  return (
    <ShortcutsContext.Provider value={ctx}>
      {children}

      {/* Quick Search / Global Search / Find modal */}
      <Dialog open={!!quickMode} onOpenChange={(o) => !o && setQuickMode(null)}>
        <DialogContent className="max-w-xl p-0 gap-0" data-testid="quick-search-modal" aria-describedby={undefined}>
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Search className="w-4 h-4"/>
              {QUICK_MODES[quickMode]?.title || "Search"}
              <Badge className="ml-auto bg-slate-100 text-slate-700 rounded-sm text-[10px]">{QUICK_MODES[quickMode]?.hint}</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="p-3 border-b">
            <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey}
              placeholder="Type name, mobile, UHID or ABHA…" className="h-10 text-sm"
              data-testid="quick-search-input"/>
            <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-3">
              <span>↑↓ to navigate · Enter to select · Esc to close</span>
              {["opd","ipd","bill"].includes(quickMode) && q.trim().length >= 2 && results.length === 0 && (
                <span className="text-blue-700">Enter → register &quot;{q.trim()}&quot;</span>
              )}
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {results.length === 0 && q.trim().length >= 2 && (
              <div className="p-6 text-center text-xs text-slate-500">No matches. Refine your query.</div>
            )}
            {results.length === 0 && q.trim().length < 2 && (
              <div className="p-6 text-center text-xs text-slate-400">Type at least 2 characters.</div>
            )}
            {results.map((p, idx) => (
              <button key={p.id} type="button"
                onClick={() => pick(p)}
                data-testid={`quick-result-${p.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left border-b ${idx === hi ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><User className="w-4 h-4"/></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-3">
                    <span><Hash className="w-3 h-3 inline"/> {p.uhid || "—"}</span>
                    <span><Phone className="w-3 h-3 inline"/> {p.mobile || "—"}</span>
                    {p.abha_number && <span className="font-mono">{p.abha_number}</span>}
                    <span>{p.age || "—"}/{p.gender || "—"}</span>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400"/>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Help modal */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-md" data-testid="shortcuts-help-modal" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Keyboard className="w-4 h-4"/> Keyboard shortcuts</DialogTitle>
          </DialogHeader>
          <div className="text-xs">
            {SHORTCUTS.map((s) => (
              <div key={s.keys} className="flex items-center justify-between py-1.5 border-b last:border-0">
                <span className="text-slate-600">{s.label}</span>
                <kbd className="px-2 py-0.5 border rounded-sm bg-slate-50 font-mono text-[10px]">{s.keys}</kbd>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </ShortcutsContext.Provider>
  );
}
